import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Gift, Loader2, CheckCircle, AlertCircle, Eye, EyeOff, ArrowRight, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

const COLORS = {
  purple: '#4A0A66',
  pink: '#DF1283',
  orange: '#FF7901',
  cyan: '#0FFFE2',
  gold: '#FFD700'
};

const RedeemCodePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillCode = searchParams.get('code') || '';
  const { checkAuth } = useAuth();
  const hasTrackedPageView = useRef(false);
  
  const [step, setStep] = useState('code'); // 'code', 'details', 'success'
  const [accessCode, setAccessCode] = useState(prefillCode);
  const [validating, setValidating] = useState(false);
  const [codeInfo, setCodeInfo] = useState(null);
  const [codeError, setCodeError] = useState('');
  
  // User details
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Track partnership events
  const trackEvent = useCallback(async (eventType, code = accessCode) => {
    if (!code) return;
    
    try {
      // Get UTM params from URL
      const utmSource = searchParams.get('utm_source');
      const utmMedium = searchParams.get('utm_medium');
      const utmCampaign = searchParams.get('utm_campaign');
      
      await axios.post(`${API}/public/partnership/track`, {
        access_code: code,
        event_type: eventType,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
      });
    } catch (err) {
      // Silently fail - tracking should never break the user experience
      console.debug('Tracking event failed:', err);
    }
  }, [accessCode, searchParams]);

  // Track page view on mount (only once)
  useEffect(() => {
    if (!hasTrackedPageView.current) {
      hasTrackedPageView.current = true;
      // If we have a prefilled code, track with that code
      if (prefillCode) {
        trackEvent('page_view', prefillCode);
      } else {
        // Track generic page view - will be associated when code is entered
        trackEvent('page_view', 'UNKNOWN');
      }
    }
  }, [prefillCode, trackEvent]);

  // Auto-validate if code is prefilled
  useEffect(() => {
    if (prefillCode) {
      validateCode(prefillCode);
    }
  }, [prefillCode]);

  // Get brand color from partner or default
  const brandColor = codeInfo?.brand_color || COLORS.pink;
  const hasCustomBranding = codeInfo?.client_logo_url || codeInfo?.brand_color;

  const validateCode = async (code) => {
    if (!code || code.length < 3) {
      setCodeError('Please enter a valid access code');
      return;
    }
    
    setValidating(true);
    setCodeError('');
    
    // Track code entry attempt
    trackEvent('code_entered', code);
    
    try {
      const response = await axios.get(`${API}/public/validate-access-code/${code.toUpperCase()}`);
      
      if (response.data.valid) {
        setCodeInfo(response.data);
        setStep('details');
        // Track successful validation
        trackEvent('code_validated', code);
      } else {
        setCodeError(response.data.error || 'Invalid access code');
      }
    } catch (err) {
      setCodeError('Error validating code. Please try again.');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!firstName.trim()) {
      toast.error('Please enter your first name');
      return;
    }
    if (!lastName.trim()) {
      toast.error('Please enter your last name');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setSubmitting(true);
    
    // Track form submission attempt
    trackEvent('form_started');
    
    try {
      const response = await axios.post(`${API}/public/redeem-access-code`, {
        access_code: accessCode,
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password: password
      });
      
      if (response.data.success) {
        // Track successful completion
        trackEvent('form_completed');
        
        setStep('success');
        toast.success('Account created successfully!');
        
        // Hydrate AuthContext with the new user session before navigating
        // Use forceCheck=true to bypass the localStorage/cookie optimization
        // since HttpOnly cookies aren't visible to JavaScript
        await checkAuth(true);
        
        // Redirect based on partnership settings
        const skipOnboarding = response.data.skip_onboarding;
        setTimeout(() => {
          if (skipOnboarding) {
            navigate('/dashboard'); // Go directly to dashboard (skip onboarding)
          } else {
            navigate('/onboarding'); // Go through onboarding
          }
        }, 3000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to create account';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const tierLabels = {
    free: 'Free',
    premium: 'Premium',
    founding_member: 'Founding Member'
  };

  return (
    <div 
      className="min-h-screen w-full flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, ${COLORS.purple} 0%, #0D0015 50%, #1a0a2e 100%)`
      }}
    >
      <div className="w-full max-w-lg px-4">
        {/* Logo - Larger size */}
        <div className="text-center mb-10">
          <img 
            src="/logo-color.png" 
            alt="UnCloned Journal" 
            className="h-20 mx-auto"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>

        {/* Step: Enter Code */}
        {step === 'code' && (
          <div 
            className="rounded-2xl p-10"
            style={{
              background: 'rgba(45, 6, 62, 0.6)',
              border: '1px solid rgba(74, 10, 102, 0.5)',
              backdropFilter: 'blur(20px)'
            }}
          >
            <div className="text-center mb-6">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: `linear-gradient(135deg, ${COLORS.pink}, ${COLORS.orange})` }}
              >
                <Gift className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Redeem Access Code</h1>
              <p className="text-white/60">Enter your organization&apos;s access code to get started</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-white/70 block mb-2">Access Code</label>
                <Input
                  value={accessCode}
                  onChange={(e) => {
                    setAccessCode(e.target.value.toUpperCase());
                    setCodeError('');
                  }}
                  placeholder="Enter your code (e.g., WELLNESS2025)"
                  className="bg-[#1a0527] border-[#4A0A66] text-white text-center text-lg tracking-widest uppercase"
                  style={{ letterSpacing: '0.2em' }}
                  data-testid="access-code-input"
                />
                {codeError && (
                  <p className="text-red-400 text-sm mt-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {codeError}
                  </p>
                )}
              </div>

              <Button
                onClick={() => validateCode(accessCode)}
                disabled={validating || !accessCode}
                className="w-full py-6 text-lg font-semibold"
                style={{ 
                  background: `linear-gradient(135deg, ${COLORS.pink}, ${COLORS.orange})`,
                  opacity: validating || !accessCode ? 0.6 : 1
                }}
                data-testid="validate-code-btn"
              >
                {validating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Validating...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>

            <p className="text-center text-white/40 text-sm mt-6">
              Having trouble with your access code?{' '}
              <span className="text-[#0FFFE2]">Contact your organization administrator</span>
            </p>
          </div>
        )}

        {/* Step: Enter Details */}
        {step === 'details' && codeInfo && (
          <div 
            className="rounded-2xl p-8 md:p-10 max-w-lg mx-auto"
            style={{
              background: 'rgba(45, 6, 62, 0.6)',
              border: `1px solid ${hasCustomBranding ? brandColor + '50' : 'rgba(74, 10, 102, 0.5)'}`,
              backdropFilter: 'blur(20px)'
            }}
          >
            {/* Organization badge with custom branding - Enhanced */}
            <div 
              className="text-center mb-8 p-6 rounded-2xl"
              style={{ 
                background: hasCustomBranding ? `${brandColor}12` : 'rgba(15, 255, 226, 0.08)', 
                border: `1px solid ${hasCustomBranding ? brandColor + '40' : 'rgba(15, 255, 226, 0.25)'}` 
              }}
            >
              {/* Partner Logo - Larger size */}
              {codeInfo.client_logo_url ? (
                <div className="mb-4">
                  <img 
                    src={codeInfo.client_logo_url} 
                    alt={codeInfo.organization_name}
                    className="h-16 md:h-20 mx-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.nextSibling.style.display = 'flex';
                    }}
                  />
                </div>
              ) : null}
              <div 
                className="items-center justify-center mb-3" 
                style={{ display: codeInfo.client_logo_url ? 'none' : 'flex' }}
              >
                <Building2 
                  className="w-12 h-12" 
                  style={{ color: hasCustomBranding ? brandColor : COLORS.cyan }} 
                />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{codeInfo.organization_name}</h3>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <span 
                  className="px-3 py-1 rounded-full text-sm font-medium"
                  style={{ 
                    background: hasCustomBranding ? `${brandColor}25` : 'rgba(15, 255, 226, 0.15)',
                    color: hasCustomBranding ? brandColor : COLORS.cyan
                  }}
                >
                  {tierLabels[codeInfo.tier] || codeInfo.tier} Access
                </span>
                {codeInfo.show_seats_remaining !== false && codeInfo.seats_remaining && (
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-medium bg-white/10 text-white/80"
                  >
                    {codeInfo.seats_remaining} spots left
                  </span>
                )}
              </div>
            </div>

            <h2 className="text-xl font-bold text-white text-center mb-6">Create Your Account</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/70 block mb-1">First Name</label>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First"
                    className="bg-[#1a0527] border-[#4A0A66] text-white"
                    required
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/70 block mb-1">Last Name</label>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last"
                    className="bg-[#1a0527] border-[#4A0A66] text-white"
                    required
                    data-testid="last-name-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-white/70 block mb-1">Email Address</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-[#1a0527] border-[#4A0A66] text-white"
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="text-xs text-white/70 block mb-1">Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 8 characters"
                    className="bg-[#1a0527] border-[#4A0A66] text-white pr-10"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-white/70 block mb-1">Confirm Password</label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="bg-[#1a0527] border-[#4A0A66] text-white"
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full py-6 text-lg font-semibold mt-6"
                style={{ 
                  background: hasCustomBranding 
                    ? brandColor 
                    : `linear-gradient(135deg, ${COLORS.pink}, ${COLORS.orange})`,
                  opacity: submitting ? 0.6 : 1
                }}
                data-testid="create-account-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create My Account
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <button
              onClick={() => {
                setStep('code');
                setCodeInfo(null);
              }}
              className="w-full text-center text-white/50 text-sm mt-4 hover:text-white/70"
            >
              ← Use a different code
            </button>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div 
            className="rounded-2xl p-8 text-center"
            style={{
              background: 'rgba(45, 6, 62, 0.6)',
              border: '1px solid rgba(15, 255, 226, 0.5)',
              backdropFilter: 'blur(20px)'
            }}
          >
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: 'rgba(15, 255, 226, 0.2)' }}
            >
              <CheckCircle className="w-10 h-10" style={{ color: COLORS.cyan }} />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">Welcome to UnCloned®!</h2>
            <p className="text-white/70 mb-6">
              Your account has been created. Redirecting you to get started...
            </p>
            
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: COLORS.cyan }} />
              <span className="text-[#0FFFE2]">Loading your journal...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RedeemCodePage;
